/**
 * Applies the server's search semantics to an in-memory list of in-flight items.
 *
 * Two callers need this: the mock provider, and the degraded fallback path that runs when the
 * paged `/health/search` endpoint is missing and we only have the legacy unpaged snapshot. Keeping
 * one implementation means the fallback view behaves the same as the real one rather than subtly
 * differently.
 */

import {
    SreInflightItem,
    SreHealthMetric,
    SreInflightSearchQuery,
    SreInflightSearchResponse,
    SreStageCatalogEntry,
    SRE_DEFAULT_PAGE_SIZE,
    SRE_INFLIGHT_STAGE_STATES,
    SRE_STAGE_STATE_NAMES,
} from '../models/SreHealth';

/** Matches the server's COALESCE(StagedInLowshare, WrittenTime) for builds. */
export const inflightAgeTimestamp = (item: SreInflightItem): number =>
    new Date(item.stagedInLowshare ?? item.writtenTime).getTime();

export const filterInflightItems = (
    items: SreInflightItem[],
    query: SreInflightSearchQuery
): SreInflightItem[] => {
    const stageStates = query.stageState?.length
        ? query.stageState
        : SRE_INFLIGHT_STAGE_STATES;
    const search = query.search?.toLowerCase();
    const agedBefore = query.agedOlderThanHours
        ? Date.now() - query.agedOlderThanHours * 3600_000
        : undefined;

    return items.filter((item) => {
        if (!stageStates.includes(item.stageStateNumber)) return false;
        if (query.stage?.length && !query.stage.includes(item.stageNumber))
            return false;
        if (
            query.uniqueIdentifier &&
            item.uniqueIdentifier !== query.uniqueIdentifier
        )
            return false;
        if (search && !item.uniqueIdentifier.toLowerCase().includes(search))
            return false;
        if (
            query.orchestrationType &&
            item.orchestrationType !== query.orchestrationType
        )
            return false;
        if (query.pipeline && item.pipeline !== query.pipeline) return false;
        if (query.region && item.region !== query.region) return false;
        if (
            query.minRetryCount !== undefined &&
            item.retryCount < query.minRetryCount
        )
            return false;
        if (
            agedBefore !== undefined &&
            inflightAgeTimestamp(item) >= agedBefore
        )
            return false;
        return true;
    });
};

const sortValue = (
    item: SreInflightItem,
    sortBy: string
): number | string | null => {
    switch (sortBy) {
        case 'uniqueIdentifier':
            return item.uniqueIdentifier;
        case 'stage':
            return item.stageNumber;
        case 'stageState':
            return item.stageStateNumber;
        case 'retryCount':
            return item.retryCount;
        case 'lastModifiedTime':
            return new Date(item.lastModifiedTime).getTime();
        case 'stagedInLowshare':
            return item.stagedInLowshare
                ? new Date(item.stagedInLowshare).getTime()
                : null;
        case 'writtenTime':
        default:
            return new Date(item.writtenTime).getTime();
    }
};

export const sortInflightItems = (
    items: SreInflightItem[],
    query: SreInflightSearchQuery
): SreInflightItem[] => {
    const sortBy = query.sortBy ?? 'writtenTime';
    const direction = query.sortDirection === 'desc' ? -1 : 1;

    return [...items].sort((a, b) => {
        const left = sortValue(a, sortBy);
        const right = sortValue(b, sortBy);

        // Nulls last regardless of direction, matching the server's CASE WHEN prefix.
        if (left === null && right === null) return a.id - b.id;
        if (left === null) return 1;
        if (right === null) return -1;

        if (left < right) return -1 * direction;
        if (left > right) return 1 * direction;

        // Id tiebreaker keeps paging stable when the sort column ties.
        return a.id - b.id;
    });
};

const countBy = <K extends string | number>(
    items: SreInflightItem[],
    keyOf: (item: SreInflightItem) => K
): Map<K, number> => {
    const map = new Map<K, number>();
    items.forEach((item) => {
        const key = keyOf(item);
        map.set(key, (map.get(key) ?? 0) + 1);
    });
    return map;
};

export const buildInflightFacets = (
    filtered: SreInflightItem[],
    catalog: SreStageCatalogEntry[]
): SreInflightSearchResponse['facets'] => ({
    byStage: Array.from(countBy(filtered, (i) => i.stageNumber).entries())
        .map(([number, count]) => {
            const entry = catalog.find((c) => c.number === number);
            return {
                name: entry?.name ?? String(number),
                number,
                order: entry?.order ?? null,
                count,
            };
        })
        .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999)),

    byStageState: Array.from(
        countBy(filtered, (i) => i.stageStateNumber).entries()
    )
        .map(([number, count]) => ({
            name: SRE_STAGE_STATE_NAMES[number] ?? String(number),
            number,
            count,
        }))
        .sort((a, b) => a.number - b.number),

    byOrchestrationType: Array.from(
        countBy(filtered, (i) => i.orchestrationType || 'Unknown').entries()
    )
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
});

/**
 * Runs the full search against an in-memory dataset.
 *
 * @param allItems Every in-flight item, used for the fleet-wide summary counts.
 * @param query The active filter, sort and paging options.
 * @param catalog The stage catalog, used to name and order the stage facets.
 * @param fleetMetrics Existing server metrics, when adapting a legacy snapshot.
 */
export const runLocalInflightQuery = (
    allItems: SreInflightItem[],
    query: SreInflightSearchQuery,
    catalog: SreStageCatalogEntry[],
    fleetMetrics?: SreHealthMetric[]
): SreInflightSearchResponse => {
    const filtered = sortInflightItems(
        filterInflightItems(allItems, query),
        query
    );

    const pageSize = Math.min(
        200,
        Math.max(1, Math.floor(query.pageSize ?? SRE_DEFAULT_PAGE_SIZE))
    );
    const pageNumber = Math.max(1, Math.floor(query.page ?? 1));
    const start = (pageNumber - 1) * pageSize;

    const fleetInflight = allItems.filter((i) =>
        SRE_INFLIGHT_STAGE_STATES.includes(i.stageStateNumber)
    );

    const seen = new Set<string>();
    const duplicates = new Set<string>();
    fleetInflight.forEach((item) => {
        if (seen.has(item.uniqueIdentifier)) {
            duplicates.add(item.uniqueIdentifier);
        }
        seen.add(item.uniqueIdentifier);
    });

    const distinctOlderThan = (days: number): number => {
        const cutoff = Date.now() - days * 86400_000;
        return new Set(
            fleetInflight
                .filter((i) => inflightAgeTimestamp(i) < cutoff)
                .map((i) => i.uniqueIdentifier)
        ).size;
    };

    const buildMetrics = (): SreHealthMetric[] => {
        const metricGroups = new Map<
            string,
            {
                first: SreInflightItem;
                count: number;
                retrySum: number;
                maxRetryCount: number;
            }
        >();
        filtered.forEach((item) => {
            const key = JSON.stringify([
                item.orchestrationType,
                item.stageNumber,
                item.stageStateNumber,
            ]);
            const retryCount = item.retryCount;
            const group = metricGroups.get(key);
            if (group) {
                group.count++;
                group.retrySum += retryCount;
                group.maxRetryCount = Math.max(group.maxRetryCount, retryCount);
            } else {
                metricGroups.set(key, {
                    first: item,
                    count: 1,
                    retrySum: retryCount,
                    maxRetryCount: retryCount,
                });
            }
        });
        return Array.from(metricGroups.values()).map(
            ({ first, count, retrySum, maxRetryCount }) => ({
                orchestrationType: first.orchestrationType,
                stage: first.stage,
                stageNumber: first.stageNumber,
                stageOrder: first.stageOrder,
                stageState: first.stageState,
                stageStateNumber: first.stageStateNumber,
                count,
                avgRetryCount: retrySum / count,
                maxRetryCount,
            })
        );
    };
    return {
        items: filtered.slice(start, start + pageSize),
        metrics: fleetMetrics ?? buildMetrics(),
        metricsScope: fleetMetrics ? 'fleet' : 'filtered',
        page: {
            pageNumber,
            pageSize,
            totalCount: filtered.length,
            totalPages: Math.ceil(filtered.length / pageSize),
        },
        facets: buildInflightFacets(filtered, catalog),
        summary: {
            matchingCount: filtered.length,
            fleetInflightCount: fleetInflight.length,
            duplicateCount: duplicates.size,
            olderThanOneDayCount: distinctOlderThan(1),
            olderThanSixDaysCount: distinctOlderThan(6),
        },
        stageCatalog: catalog,
        queriedAtUtc: new Date().toISOString(),
    };
};
